import React, { useState, useRef, useEffect } from "react";
import { cn, parseEntityRefs } from "@/lib/utils";
import {
    Send,
    Loader2,
    Menu,
    BotMessageSquare,
    ThumbsUp,
    ThumbsDown,
    AtSign,
    User,
    Calendar,
    Trophy,
} from "lucide-react";
import { chatApi, ConversationDto, ConversationDetailsDto } from "@/api/chat";
import { feedbackApi } from "@/api/feedback";
import { ConversationSidebar } from "@/components/chat/ConversationSidebar";
import { EntityContextMenu, SelectedEntity } from "@/components/chat/EntityContextMenu";
import { EntityInput } from "@/components/chat/EntityInput";
import { MarkdownRenderer } from "@/components/chat/MarkdownRenderer";
import { logger } from "@/lib/logger";
import { useSelectedMatch } from "@/context/matchContext";
import { useSelectedTeam } from "@/context/teamContext";
import { useQuery } from "@tanstack/react-query";
import { tournamentsQueryOptions } from "@/api/tournaments";
import { matchKpisQueryOptions } from "@/api/ranking";
import { useIsMobile } from "@/hooks/use-mobile";
import { useOnboarding } from "@/components/general/Onboarding";
import { setChatSidebarClose } from "@/components/general/Onboarding/steps/chatSteps";

interface Message {
    id: string;
    content: string;
    sender: "user" | "vally";
    timestamp: Date;
    gameSummaryId?: number;
    userFeedback?: number | null;
    entityReferences?: Array<EntityReference>;
}

type EntityReference = {
    id: string;
    type: string;
    entityId: number;
    displayName: string;
    start?: number;
    end?: number;
};

export const ChatPage = () => {
    const [conversations, setConversations] = useState<ConversationDto[]>([]);
    const [selectedConversation, setSelectedConversation] = useState<ConversationDetailsDto | null>(null);
    const [messages, setMessages] = useState<Message[]>([]);
    const [inputValue, setInputValue] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [isLoadingConversations, setIsLoadingConversations] = useState(true);
    const [isCreatingConversation, setIsCreatingConversation] = useState(false);
    const [isEntityMenuOpen, setIsEntityMenuOpen] = useState(false);
    const [entityMenuPosition, setEntityMenuPosition] = useState({ x: 0, y: 0 });
    const [cursorPosition, setCursorPosition] = useState(0);
    const [entityReferences, setEntityReferences] = useState<Array<EntityReference>>([]);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const isMobile = useIsMobile();
    const { registerBeforeChatTour } = useOnboarding();

    useEffect(() => {
        const cleanup = registerBeforeChatTour(() => {
            if (isMobile) setIsSidebarOpen(true);
        });
        setChatSidebarClose(() => setIsSidebarOpen(false));
        return () => {
            cleanup();
            setChatSidebarClose(null);
        };
    }, [registerBeforeChatTour, isMobile]);

    const { matchIds } = useSelectedMatch();
    const { teamId } = useSelectedTeam();
    const { data: tournaments, isLoading: isTournamentsLoading } = useQuery(tournamentsQueryOptions(teamId));
    const { data: kpiData, isLoading: isKpisLoading } = useQuery(
        matchKpisQueryOptions(matchIds.length > 0 ? matchIds : [], teamId)
    );

    const isMatchLoading = isTournamentsLoading || isKpisLoading;
    const selectedMatches =
        tournaments?.flatMap((tournament) =>
            tournament.matches
                .filter((match) => matchIds.includes(match.id))
                .map((match) => ({ ...match, tournamentName: tournament.name }))
        ) || [];

    const fallbackMatch =
        selectedMatches.length === 0 && kpiData?.matches && kpiData.matches.length > 0
            ? tournaments
                  ?.find((tournament) => tournament.matches.some((match) => match.id === kpiData.matches[0].matchId))
                  ?.matches.find((match) => match.id === kpiData.matches[0].matchId)
            : null;

    const displayMatches = selectedMatches.length > 0 ? selectedMatches : fallbackMatch ? [fallbackMatch] : [];

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        loadConversations();
        inputRef.current?.focus();
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        const resize = () => {
            const textarea = inputRef.current;
            if (!textarea) return;
            textarea.style.height = "auto";
            if (inputValue.length === 0) {
                const original = textarea.value;
                textarea.value = textarea.placeholder;
                textarea.style.height = Math.min(textarea.scrollHeight, 200) + "px";
                textarea.value = original;
            } else {
                textarea.style.height = Math.min(textarea.scrollHeight, 200) + "px";
            }
        };
        const raf = requestAnimationFrame(resize);
        return () => cancelAnimationFrame(raf);
    }, [inputValue, selectedConversation]);

    const loadConversations = async () => {
        try {
            setIsLoadingConversations(true);
            const conversationsData = await chatApi.getConversations();
            setConversations(conversationsData);

            if (conversationsData.length > 0 && !selectedConversation) {
                const mostRecent = conversationsData[0];
                await selectConversation(mostRecent.conversationId);
            }
        } catch (error) {
            logger.error("Failed to load conversations:", error);
        } finally {
            setIsLoadingConversations(false);
        }
    };

    const beautifyMessageContent = (content: string) => {
        let displayContent = content;
        const entityRefPattern = /(\w+):\{(\d+)\|([^}]+)\}/g;
        const entityReferences: Array<EntityReference> = [];
        let match;
        while ((match = entityRefPattern.exec(content)) !== null) {
            const [fullMatch, type, entityId, displayName] = match;
            const entityRefId = `entity_${type}_${entityId}`;
            entityReferences.push({
                id: entityRefId,
                type,
                entityId: parseInt(entityId),
                displayName,
            });
            displayContent = displayContent.replace(fullMatch, ``);
        }
        // Also handle bare type:{id} refs without display name
        displayContent = displayContent.replace(/(\w+):\{(\d+)\}/g, (_full, _type, id) => `#${id}`);
        return { displayContent, entityReferences };
    };

    const selectConversation = async (conversationId: string) => {
        try {
            const conversation = await chatApi.getConversation(conversationId);
            setSelectedConversation(conversation);

            const uiMessages: Message[] = conversation.messages.map((msg) => {
                const { displayContent, entityReferences } = beautifyMessageContent(msg.content);
                return {
                    id: msg.id.toString(),
                    content: displayContent,
                    sender: msg.role === "user" ? "user" : "vally",
                    timestamp: new Date(msg.createdAt),
                    entityReferences: entityReferences.length > 0 ? entityReferences : undefined,
                };
            });
            setMessages(uiMessages);
        } catch (error) {
            logger.error("Failed to load conversation:", error);
        }
    };

    const createNewConversation = async () => {
        if (isCreatingConversation) return;

        try {
            setIsCreatingConversation(true);
            const newConversation = await chatApi.createConversation();
            await loadConversations();
            await selectConversation(newConversation.conversationId);
        } catch (error) {
            logger.error("Failed to create conversation:", error);
        } finally {
            setIsCreatingConversation(false);
        }
    };

    const deleteConversation = async (conversationId: string) => {
        try {
            await chatApi.deleteConversation(conversationId);
            await loadConversations();

            if (selectedConversation?.conversationId === conversationId) {
                setSelectedConversation(null);
                setMessages([]);

                const updatedConversations = conversations.filter((c) => c.conversationId !== conversationId);
                if (updatedConversations.length > 0) {
                    await selectConversation(updatedConversations[0].conversationId);
                }
            }
        } catch (error) {
            logger.error("Failed to delete conversation:", error);
        }
    };

    const convertToBackendFormat = (text: string, entityReferences: Array<EntityReference>): string =>
        entityReferences.reduce((text, entityRef) => {
            return `${entityRef.type}:{${entityRef.entityId}|${entityRef.displayName}} ${text}`;
        }, text);

    const handleSendMessage = async () => {
        if (!inputValue.trim() || isLoading || !selectedConversation) {
            if (!selectedConversation) {
                await createNewConversation();
                return;
            }
            return;
        }

        const displayContent = inputValue.trim();
        const backendContent = convertToBackendFormat(displayContent, entityReferences);
        const currentEntityRefs = entityReferences;

        const userMessage: Message = {
            id: Date.now().toString(),
            content: displayContent,
            sender: "user",
            timestamp: new Date(),
            entityReferences: currentEntityRefs,
        };

        setMessages((prev) => [...prev, userMessage]);
        setInputValue("");
        setIsLoading(true);
        setEntityReferences([]);

        try {
            const response = await chatApi.sendMessage(backendContent, selectedConversation.conversationId);

            const vallyResponse: Message = {
                id: (Date.now() + 1).toString(),
                content: response.output || "I couldn't process that request. Please try again.",
                sender: "vally",
                timestamp: new Date(),
            };
            setMessages((prev) => [...prev, vallyResponse]);

            await loadConversations();
        } catch (error) {
            logger.error("Error sending message:", error);
            const errorMessage: Message = {
                id: (Date.now() + 1).toString(),
                content: "Sorry, I'm having trouble connecting to the server. Please try again later.",
                sender: "vally",
                timestamp: new Date(),
            };
            setMessages((prev) => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        } else if (e.key === "Escape" && isEntityMenuOpen) {
            setIsEntityMenuOpen(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        const selectionStart = e.target.selectionStart;

        setInputValue(value);
        setCursorPosition(selectionStart);

        if (value[selectionStart - 1] === "@" && !isEntityMenuOpen) {
            const rect = e.target.getBoundingClientRect();
            const lineHeight = 20;
            const lines = value.substring(0, selectionStart).split("\n");
            const currentLine = lines.length - 1;
            const currentColumn = lines[lines.length - 1].length;

            const x = rect.left + Math.min(currentColumn * 8, rect.width - 300);
            const y = rect.top + currentLine * lineHeight;

            setEntityMenuPosition({ x, y });
            setIsEntityMenuOpen(true);
        }
    };

    const handleEntitySelect = (entities: SelectedEntity[]) => {
        if (!inputRef.current || entities.length === 0) return;
        const beforeCursor = inputValue.substring(0, cursorPosition);
        let atIndex = beforeCursor.lastIndexOf("@");

        if (atIndex === -1) {
            atIndex = cursorPosition;
        }
        let currentPos = atIndex;
        const newEntityRefs = entities
            .map((entity, index) => {
                const entityTypeLabel =
                    entity.type === "player" ? "player" : entity.type === "match" ? "match" : "tournament";
                const text = `@${entity.name} (${entityTypeLabel}:${entity.id})`;
                const ref = {
                    id: `entity_${Date.now()}_${index}`,
                    type: entity.type,
                    entityId: entity.id,
                    displayName: entity.name,
                    start: currentPos,
                    end: currentPos + text.length,
                };
                currentPos += text.length + 1;
                return ref;
            })
            .filter(
                (ref) =>
                    !entityReferences.some(
                        (existingRef) => existingRef.type === ref.type && existingRef.entityId === ref.entityId
                    )
            );

        const newValue = inputValue.substring(0, atIndex) + inputValue.substring(atIndex + 1);
        setInputValue(newValue);
        setEntityReferences((prev) => [...prev, ...newEntityRefs]);
        setIsEntityMenuOpen(false);
    };

    const handleFeedback = async (messageId: string, gameSummaryId: number | undefined, rating: number) => {
        try {
            const entityType = gameSummaryId ? "game_summary" : "conversation_message";
            const entityId = gameSummaryId ? gameSummaryId.toString() : messageId;
            const feedbackValue = rating === 1;

            await feedbackApi.submitFeedback({
                entityType,
                entityId,
                feedbackValue,
            });

            setMessages((prevMessages) =>
                prevMessages.map((msg) => (msg.id === messageId ? { ...msg, userFeedback: rating } : msg))
            );
        } catch (error) {
            logger.error("Failed to submit feedback:", error);
        }
    };

    const handleDeleteRef = (refId: string) => {
        setEntityReferences((prev) => prev.filter((ref) => ref.id !== refId));
    };

    const getEntityBadgeStyle = (): React.CSSProperties => ({
        background: "var(--color-secondary-token)",
    });

    const getEntityIcon = (type: string) => {
        if (type === "player") return <User className="w-3 h-3" />;
        if (type === "tournament") return <Trophy className="w-3 h-3" />;
        return <Calendar className="w-3 h-3" />;
    };

    useEffect(() => {
        if (isMatchLoading || displayMatches.length <= 0 || !inputRef.current) return;

        handleEntitySelect(
            displayMatches.map((match) => ({
                id: match.id,
                type: "match",
                name: `${match.homeTeam.shortName} vs ${match.awayTeam.shortName}`,
            }))
        );
    }, [isMatchLoading, displayMatches.length, inputRef.current]);

    return (
        <div id="onboarding-chat" className="h-full bg-surface-container-lowest flex overflow-hidden">
            <div
                id="onboarding-chat-sidebar"
                className={cn(
                    "transition-all duration-300 ease-in-out",
                    isSidebarOpen ? "translate-x-0" : "-translate-x-full",
                    "fixed lg:relative lg:translate-x-0 z-30 h-full"
                )}
            >
                <ConversationSidebar
                    conversations={conversations}
                    selectedConversationId={selectedConversation?.conversationId || null}
                    onSelectConversation={selectConversation}
                    onCreateConversation={createNewConversation}
                    onDeleteConversation={deleteConversation}
                    isLoading={isLoadingConversations}
                    isCreatingConversation={isCreatingConversation}
                />
            </div>
            {isSidebarOpen && (
                <div className="fixed inset-0 bg-black/50 z-20 lg:hidden" onClick={() => setIsSidebarOpen(false)} />
            )}
            <div id="onboarding-chat-main" className="flex-1 flex flex-col min-w-0">
                <div className="border-b border-outline-variant/20 bg-surface-container px-4 py-2.5">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                            className="lg:hidden p-2 rounded-md hover:bg-surface-container transition-colors"
                        >
                            <Menu className="w-5 h-5 text-on-surface-variant" />
                        </button>

                        <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center shrink-0">
                            <BotMessageSquare className="text-white h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h1 className="text-base font-headline font-black text-primary truncate">
                                {selectedConversation ? parseEntityRefs(selectedConversation.title) : "Våll-e Chat"}
                            </h1>
                            <p className="text-[11px] text-on-surface-variant font-medium">
                                {selectedConversation
                                    ? `${messages.length} messages`
                                    : "Your Hockey Analytics Assistant"}
                            </p>
                        </div>
                        <span
                            className="text-white text-[10px] font-headline font-black px-2.5 py-1 rounded-sm uppercase tracking-widest flex-shrink-0"
                            style={{ background: "var(--color-secondary-token)" }}
                        >
                            BETA
                        </span>
                    </div>
                </div>

                <div className="flex-1 flex flex-col overflow-hidden">
                    {selectedConversation ? (
                        <>
                            <div className="flex-1 overflow-y-auto px-4 py-3 min-h-0 relative">
                                {messages.length === 0 && !isLoading ? (
                                    <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-4">
                                        <div className="w-14 h-14 bg-primary rounded-full flex items-center justify-center mb-3">
                                            <BotMessageSquare className="text-white h-7 w-7" />
                                        </div>
                                        <h3 className="text-base font-headline font-bold text-primary mb-1.5">
                                            Start a conversation
                                        </h3>
                                        <p className="text-[13px] text-on-surface-variant max-w-md">
                                            Ask me about player stats, team performance, game strategies, or anything
                                            hockey-related!
                                        </p>
                                    </div>
                                ) : (
                                    <div className="w-full space-y-3">
                                        {messages.map((message) => (
                                            <div
                                                key={message.id}
                                                className={cn(
                                                    "flex flex-col gap-1",
                                                    message.sender === "user" ? "items-end" : "items-start"
                                                )}
                                            >
                                                <div
                                                    className={cn(
                                                        "max-w-[75%] rounded-xl px-3 py-2",
                                                        message.sender === "user"
                                                            ? "text-white shadow-sm"
                                                            : "bg-surface-container-low text-on-surface shadow-sm border border-outline-variant/20"
                                                    )}
                                                    style={
                                                        message.sender === "user"
                                                            ? { background: "var(--color-primary-token)" }
                                                            : undefined
                                                    }
                                                >
                                                    <div className="text-[13px] whitespace-pre-wrap break-words">
                                                        {message.sender === "vally" ? (
                                                            <MarkdownRenderer content={message.content.trim()} />
                                                        ) : (
                                                            message.content.trim()
                                                        )}
                                                    </div>
                                                    <p
                                                        className={cn(
                                                            "text-[10px] mt-1.5",
                                                            message.sender === "user"
                                                                ? "text-white/60"
                                                                : "text-on-surface-variant"
                                                        )}
                                                    >
                                                        {message.timestamp.toLocaleTimeString([], {
                                                            hour: "2-digit",
                                                            minute: "2-digit",
                                                        })}
                                                    </p>
                                                    {message.entityReferences &&
                                                        message.entityReferences.length > 0 && (
                                                            <div
                                                                className={cn(
                                                                    "mt-2 flex items-center gap-1 overflow-x-auto pb-2",
                                                                    isMobile &&
                                                                        "[scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                                                                )}
                                                            >
                                                                {message.entityReferences.map((ref) => (
                                                                    <span
                                                                        key={ref.id}
                                                                        className="flex items-center gap-1 text-white text-[10px] font-headline font-bold px-2 py-0.5 rounded-sm uppercase tracking-wide whitespace-nowrap shrink-0"
                                                                        style={getEntityBadgeStyle()}
                                                                    >
                                                                        {ref.displayName}
                                                                        {getEntityIcon(ref.type)}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                </div>
                                                {message.sender === "vally" && (
                                                    <div className="flex gap-1 px-2">
                                                        <button
                                                            onClick={() =>
                                                                handleFeedback(message.id, message.gameSummaryId, 1)
                                                            }
                                                            disabled={
                                                                message.userFeedback !== undefined &&
                                                                message.userFeedback !== null
                                                            }
                                                            className={cn(
                                                                "p-1.5 rounded-md transition-all",
                                                                message.userFeedback === 1
                                                                    ? "bg-general-green/20 text-general-green"
                                                                    : "hover:bg-surface-container text-on-surface-variant hover:text-general-green",
                                                                "disabled:opacity-50"
                                                            )}
                                                            title="Helpful"
                                                        >
                                                            <ThumbsUp className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() =>
                                                                handleFeedback(message.id, message.gameSummaryId, 0)
                                                            }
                                                            disabled={
                                                                message.userFeedback !== undefined &&
                                                                message.userFeedback !== null
                                                            }
                                                            className={cn(
                                                                "p-1.5 rounded-md transition-all",
                                                                message.userFeedback === 0
                                                                    ? "bg-general-red/20 text-general-red"
                                                                    : "hover:bg-surface-container text-on-surface-variant hover:text-general-red",
                                                                "disabled:opacity-50"
                                                            )}
                                                            title="Not helpful"
                                                        >
                                                            <ThumbsDown className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                        {isLoading && (
                                            <div className="flex justify-start">
                                                <div className="max-w-[75%] rounded-xl px-3 py-2 bg-surface-container-low text-on-surface shadow-sm border border-outline-variant/20">
                                                    <div className="flex items-center gap-2">
                                                        <Loader2 className="w-4 h-4 animate-spin text-primary" />
                                                        <span className="text-[13px] text-on-surface-variant">
                                                            Thinking...
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        <div ref={messagesEndRef} />
                                    </div>
                                )}
                            </div>

                            <div className="shrink-0 border-t border-outline-variant/20 bg-surface-container px-4 py-3">
                                <div id="onboarding-chat-input" className="flex gap-2 items-end">
                                    <div className="flex flex-col gap-0 items-end flex-1 px-3 py-2 bg-white border border-outline-variant/40 rounded-xl focus-within:border-primary/40 focus-within:ring-1 focus-within:ring-primary/20 text-on-surface resize-none min-h-[44px] max-h-[200px] overflow-y-auto transition-all">
                                        <div className="flex items-center w-full gap-2">
                                            <button
                                                onClick={(e) => {
                                                    const x = e.clientX;
                                                    const y = e.clientY;
                                                    setEntityMenuPosition({ x, y });
                                                    setIsEntityMenuOpen(true);
                                                }}
                                                className="cursor-pointer shrink-0"
                                            >
                                                <AtSign className="w-4 h-4 text-on-surface-variant hover:text-primary transition-colors" />
                                            </button>
                                            <div className="relative flex-1 min-w-0">
                                                <div className="flex items-center gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                                                    {entityReferences.map((ref) => (
                                                        <div
                                                            key={ref.id}
                                                            className="flex items-center gap-1 text-white text-[10px] font-headline font-bold px-2 py-0.5 rounded-sm uppercase tracking-wide whitespace-nowrap shrink-0"
                                                            style={getEntityBadgeStyle()}
                                                        >
                                                            <button
                                                                className="cursor-pointer hover:opacity-70 transition-opacity"
                                                                onClick={() => handleDeleteRef(ref.id)}
                                                            >
                                                                ×
                                                            </button>
                                                            {ref.displayName}
                                                            {getEntityIcon(ref.type)}
                                                        </div>
                                                    ))}
                                                </div>
                                                {entityReferences.length > 0 && (
                                                    <div className="absolute right-0 top-0 h-full w-8 bg-linear-to-l from-white to-transparent pointer-events-none" />
                                                )}
                                            </div>
                                        </div>
                                        <EntityInput
                                            ref={inputRef}
                                            value={inputValue}
                                            onChange={handleInputChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Ask about player stats, team performance... Type @ to reference entities"
                                            className="px-0 py-1 w-full bg-transparent border-none rounded-lg focus:outline-none text-[13px] text-on-surface placeholder:text-on-surface-variant resize-none min-h-[28px] max-h-[200px] overflow-y-auto disabled:opacity-50"
                                            rows={1}
                                            disabled={isLoading}
                                        />
                                    </div>
                                    <button
                                        onClick={handleSendMessage}
                                        disabled={!inputValue.trim() || isLoading}
                                        className="px-4 py-2.5 text-white rounded-xl font-headline font-bold uppercase tracking-wider text-xs hover:opacity-90 transition-all flex items-center gap-2 shadow-sm disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                                        style={{ background: "var(--color-primary-token)" }}
                                    >
                                        {isLoading ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <Send className="w-4 h-4" />
                                        )}
                                        <span className="hidden sm:inline">Send</span>
                                    </button>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
                            <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mb-4">
                                <BotMessageSquare className="text-white h-8 w-8" />
                            </div>
                            <h2 className="text-xl font-headline font-black text-primary mb-2">
                                Welcome to Våll-e Chat
                            </h2>
                            <p className="text-sm text-on-surface-variant mb-6 max-w-md font-medium">
                                Your AI-powered hockey analytics assistant. Start a new conversation to analyze player
                                performance, team strategies, and game statistics.
                            </p>
                            <button
                                onClick={createNewConversation}
                                disabled={isCreatingConversation}
                                className="px-6 py-3 text-white rounded-xl font-headline font-bold uppercase tracking-wider text-xs hover:opacity-90 transition-all flex items-center gap-2 shadow-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                style={{ background: "var(--color-primary-token)" }}
                            >
                                {isCreatingConversation ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Starting Conversation...
                                    </>
                                ) : (
                                    <>
                                        <Send className="w-4 h-4" />
                                        Start New Conversation
                                    </>
                                )}
                            </button>
                        </div>
                    )}
                </div>
            </div>
            <EntityContextMenu
                isOpen={isEntityMenuOpen}
                position={entityMenuPosition}
                onSelect={handleEntitySelect}
                onClose={() => setIsEntityMenuOpen(false)}
            />
        </div>
    );
};
